/*
 * RUG-GIFT-CART — THE FIVE FREE GIFTS FOLLOW THE RUG, AUTOMATICALLY.
 *
 * ── IT IS A RECONCILER, NOT SIX HANDLERS ───────────────────────────────────
 * The requirement is six behaviours: added once and not five times when two
 * rugs go in; removed when the rug is removed; removed when the cart is
 * emptied; not removable on their own; never reachable at checkout without a
 * rug; and always exactly one of each however many rugs there are.
 *
 * Written as six handlers those are six chances to miss a path — and there are
 * a lot of paths, because a cart changes from the product form, the sticky
 * bar, the rug finder, the visualiser, the drawer's own stepper, the drawer's
 * remove button, the cart page, a back-button restore and a second tab. So
 * this states ONE invariant instead:
 *
 *     the cart holds exactly the five gifts, at one each,
 *     IF AND ONLY IF it holds at least one line that is not a gift
 *
 * and enforces it after every change and on every page load. All six fall out
 * of that, and so does the seventh nobody listed — a cart left in a bad state
 * by a session that closed mid-write.
 *
 * ── ONE REQUEST, ABSOLUTE QUANTITIES ───────────────────────────────────────
 * `/cart/update.js` is the only cart endpoint that adds, changes and removes
 * in a single call, and it takes quantities rather than deltas. MEASURED on
 * the live store before this was written:
 *
 *     one rug in the cart, then update.js with five variants NOT in it
 *         -> 6 lines, total UNCHANGED at $70.23
 *     a second rug added
 *         -> rug qty 2, every gift STILL qty 1
 *     one call setting all six to 0
 *         -> empty
 *
 * The second line is why "added once, not five times" needs no counting: an
 * absolute quantity of 1 is idempotent, so a reconciler cannot double a gift
 * however many times it runs or however many rugs arrive.
 *
 * ── WHAT COUNTS AS A RUG ───────────────────────────────────────────────────
 * Anything that is not one of the five. Not `product_type`, which the cart's
 * JSON does not carry per line in a form worth trusting, and not a tag — the
 * manifest already names the five exactly, so "not a gift" is the complement
 * of a known set rather than a second classification that could disagree with
 * the first.
 *
 * ── THE MANIFEST IS WRITTEN BY THE THING THAT CREATES THE PRODUCTS ─────────
 * `src/9_marketplace/gift_products.py` writes the five variant ids to the shop
 * metafield `rugzella.gift_cart` in the same run that creates them, and
 * `theme.liquid` renders it into `window.RUG_GIFT_CART`. So the storefront cannot be pointed
 * at a variant that does not exist, and nothing here carries a typed id or a
 * typed handle — a typed handle would be a second implementation of the
 * naming rule, which is the duplication `rug-short-name` and `fit-engine.js`
 * are each held to one copy of.
 *
 * **NO MANIFEST MEANS NO GIFTS, NEVER WRONG GIFTS.** An absent, empty or
 * unparseable manifest is read as "this shop has no gift programme" and the
 * cart is left exactly as it was found. The failure that matters here is
 * touching somebody's cart on bad information, not failing to add a gift.
 *
 * ── THE HONEST LIMIT, STATED RATHER THAN LEFT TO BE FOUND ──────────────────
 * This runs in the browser, on our own pages. A cart assembled by a crafted
 * permalink and taken straight to `/checkout` without loading a storefront
 * page of ours is never seen by it. Nothing client-side can close that, and on
 * this plan nothing server-side can either — Cart Transform needs Shopify
 * Functions and those are Plus-only, which is the finding CLAUDE.md already
 * records. What it costs is one $0 item and a shipping label; what it cannot
 * produce is a discount on anything.
 */
(function () {
  'use strict';

  var SOURCE = 'rz-gifts';

  /* ── WHAT FULFILMENT READS ON THE ORDER ───────────────────────────────────
   * Set as a CART ATTRIBUTE, so it becomes one of the order's note_attributes
   * and shows in Shopify admin beside the order a person is picking.
   *
   * THE LINES ALONE ARE NOT THE WHOLE COMMITMENT. The five gifts are real
   * products, so they are already on the order, the confirmation email and the
   * packing slip — that part needs no note. What a picker cannot see from a
   * line is that these are not stocked, are picked by hand, and are not
   * optional: an order missing them is incomplete, not short-shipped.
   *
   * REMOVED WITH THE GIFTS. An order that carries no gift must not carry a
   * note saying it does, so the attribute is set to '' whenever the reconciler
   * takes them out — Shopify treats an empty value as a removal. */
  var NOTE_KEY = 'Free gifts';
  function noteFor(n) {
    return n + ' free gift' + (n === 1 ? '' : 's') + ' are on this order. '
         + 'They are NOT STOCKED and are PICKED AND PACKED BY HAND. '
         + 'The order is not complete without them.';
  }
  var busy = false;      /* our own write must not re-trigger us */
  var GIFTS = null;      /* [{variant_id, handle, title, was}] or null */

  function manifest() {
    if (GIFTS !== null) return GIFTS;
    GIFTS = [];
    try {
      /* `window.RUG_GIFT_CART`, set by an inline script in `theme.liquid` —
       * the same shape as `RUG_FINDER_CONFIG`, which is this theme's own
       * convention for handing Liquid data to a script. An inline script runs
       * at parse time and this file is `defer`, so it is always already set. */
      var d = window.RUG_GIFT_CART;
      if (!d) return GIFTS;
      var list = (d && d.gifts) || [];
      /* EVERY ENTRY MUST CARRY A USABLE ID. A manifest half-written by a
       * failed push would otherwise put `undefined` in an updates object,
       * which Shopify answers by ignoring the key — a silent partial add. */
      GIFTS = list.filter(function (g) { return g && Number(g.variant_id) > 0; })
                  .map(function (g) {
                    return { id: Number(g.variant_id), handle: g.handle,
                             title: g.title, was: g.was };
                  });
      if (GIFTS.length !== list.length) {
        try { console.warn('[rug-gifts] manifest has entries with no variant id; ignoring them'); } catch (e) {}
      }
    } catch (e) {
      GIFTS = [];
      try { console.warn('[rug-gifts] manifest unreadable; leaving the cart alone', e); } catch (e2) {}
    }
    return GIFTS;
  }

  function ids() {
    var s = Object.create(null);
    manifest().forEach(function (g) { s[g.id] = true; });
    return s;
  }

  function readCart() {
    return fetch('/cart.js', { headers: { accept: 'application/json' },
                               credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart read');
        return r.json();
      });
  }

  /**
   * What has to change for the invariant to hold. Pure — no request, no state.
   *
   * SEPARATED FROM THE WRITE DELIBERATELY, so the rule can be exercised
   * against a cart object without touching a live cart.
   *
   * @param {object} cart  a /cart.js response
   * @returns {object} { updates, hasRug, changed }
   */
  function plan(cart) {
    var gift = ids();
    var list = manifest();
    var items = (cart && cart.items) || [];
    var hasRug = false;
    var have = Object.create(null);
    for (var i = 0; i < items.length; i++) {
      var id = Number(items[i].variant_id);
      if (gift[id]) have[id] = (have[id] || 0) + Number(items[i].quantity || 0);
      else hasRug = true;
    }
    var want = hasRug ? 1 : 0;
    var updates = {};
    for (var j = 0; j < list.length; j++) {
      var gid = list[j].id;
      if ((have[gid] || 0) !== want) updates[gid] = want;
    }
    return { updates: updates, hasRug: hasRug,
             changed: Object.keys(updates).length > 0 };
  }

  /**
   * Bring the cart into line. Safe to call as often as you like.
   *
   * @param {object} [cart] a cart already in hand, to save a read
   * @returns {Promise<object|null>} the plan that was applied, or null
   */
  function reconcile(cart) {
    if (busy || !manifest().length) return Promise.resolve(null);
    var start = cart ? Promise.resolve(cart) : readCart();
    var list = manifest();
    return start.then(function (c) {
      var p = plan(c);
      if (!p.changed) return null;
      if (!window.RugCart || !window.RugCart.update) {
        try { console.warn('[rug-gifts] RugCart.update is not loaded'); } catch (e) {}
        return null;
      }
      busy = true;
      var attrs = {};
      attrs[NOTE_KEY] = p.hasRug ? noteFor(list.length) : '';
      return window.RugCart.update(p.updates, SOURCE, attrs)
        .then(function () { return p; })
        ['finally'](function () { busy = false; });
    })['catch'](function (err) {
      busy = false;
      /* A FAILED RECONCILE IS REPORTED AND NOT RETRIED IN A LOOP. The next
       * cart change or the next page load will try again, which is a bounded
       * number of attempts rather than an unbounded one against an endpoint
       * that has just refused us. */
      try { console.warn('[rug-gifts] could not reconcile the cart', err); } catch (e) {}
      return null;
    });
  }

  /* ── WHEN IT RUNS ─────────────────────────────────────────────────────────
   * After every cart change, and on every page load.
   *
   * THE LOAD PASS IS NOT BELT AND BRACES, IT IS THE ONLY THING THAT SEES SOME
   * OF THE PATHS: a cart restored from a previous session, a second tab, a
   * back-button navigation, and — the one the requirement names — arriving at
   * the cart page holding gifts and no rug. It costs one `/cart.js` on a cart
   * that is already correct and writes nothing. */
  function onChange(event) {
    if (busy) return;
    var p = event && event.promise;
    if (!p || typeof p.then !== 'function') {
      /* An event with no promise tells us nothing about WHEN the write landed,
       * so read the cart ourselves rather than acting on a stale one. */
      reconcile();
      return;
    }
    p.then(function (res) {
      /* THE RESOLVED CART IS SHOPIFY'S OWN ANSWER TO THE WRITE THAT JUST
       * HAPPENED, so using `detail.items` saves a round trip and removes a
       * race with anything else touching the cart in the same moment. */
      var items = res && res.detail && res.detail.items;
      reconcile(items ? { items: items } : undefined);
    }, function () { /* the write failed; there is nothing to reconcile to */ });
  }

  document.addEventListener('shopify:cart:lines-update', onChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }

  window.RugGiftCart = { reconcile: reconcile, plan: plan, manifest: manifest };
})();
